import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useEpiClergySettings, useEpiClergySettingsLoaded } from '../../hooks/useEpiClergySettings';
import { BishopConsecrationData, ClergyComplete, DiaconalData, PriestingData, PrincipalPosition } from './clergy-model';
import { FormButtons } from '../form-element/form-buttons';
import { Wysiwyg } from '../wysiwyg';
import { MailDestination } from '../mail/mail-model';
import { EcdStates } from '../../../root/ecd-model';
import { getBasePath } from '../../helpers/routing';
import { EcdRoutes } from '../../constants/routes';

interface Props {
    selectedClergy: ClergyComplete;
    onNewSearch: () => void;
    onNavigateBack: () => void;
    onSelectParishById: (parishId: string | number) => void;
    onSelectClergyById: (clergyId: string | number, isReplace: boolean) => void;
    onNavigateToSendEmail: (clergyId: string | number, destination: MailDestination) => void;    
}

function ClergyDetail(props: Props) {
    const history = useHistory();
    const { clergyid } = useParams<any>();
    const { selectedClergy, onNewSearch, onNavigateBack, onSelectParishById, onSelectClergyById, onNavigateToSendEmail } = props;
    const clergySettingsLoaded = useEpiClergySettingsLoaded();
    const clergySettings = useEpiClergySettings();

    const currentlanguage = useSelector<EcdStates, string>(state => state?.globalization?.currentlanguage);    
    const basePath = getBasePath(currentlanguage);

    useEffect(() => {
		if (clergyid && !selectedClergy?.id){
            onSelectClergyById(clergyid, true);
        }
	}, []);

    if (!clergyid && !selectedClergy?.id){
        history.push(`${basePath}/${EcdRoutes.Clergy}`);        
    }

    if (!selectedClergy || !clergySettingsLoaded || !clergySettings) {
        return <LoadingCircle />;
    }

    function NavigationButtons() {
        return (<>
            <FormButtons
                disabled={false}
                submitText={clergySettings?.newSearchButton || 'New Search'}
                cancelText={clergySettings?.backButton}
                onSubmit={onNewSearch}
                onCancel={onNavigateBack}
                submitDisabled={false}
                hideCancelButton={!clergySettings?.backButton}
            />
        </>);
    }

    function EmailAction() {
        if (!selectedClergy?.email_address) return null;

        return (<>
            <div> <button className=' quickfind-detail-section-button-link'  type='button' onClick={() => onNavigateToSendEmail(
                selectedClergy.id,                
                {                
                email: selectedClergy.email_address,
                name: selectedClergy.full_name,
                itemGuid: selectedClergy.id,                
            })}> {clergySettings.sendEmailLabel} </button></div>
        </>);
    }

    function PublishedAddress() {
        if (!selectedClergy?.published_address?.address1) {
            return (<div>
                <div className='quickfind-detail-section-title'><p><strong>{clergySettings.publishedAddressTitle}</strong></p></div>
                <div> <Wysiwyg content={clergySettings.publishedAddressEmptyMessage} /></div>
            </div>);
        }

        return (<div className='quickfind-detail-section'>
            <div className='quickfind-detail-section-title'><p><strong>{clergySettings.publishedAddressTitle}</strong></p></div>
            <div className='quickfind-detail-section-inner'>
                <div>{selectedClergy?.published_address?.address1 || ''}</div>
                <div>{selectedClergy?.published_address?.address2 || ''}</div>
                <div>{selectedClergy?.published_address?.address3 || ''}</div>
                <div>{`${selectedClergy?.published_address?.city ? `${selectedClergy?.published_address?.city},` : ''} ${selectedClergy?.published_address?.state || ''} ${selectedClergy?.published_address?.postal_code || ''}`}</div>
                <div>{selectedClergy?.published_address?.country || ''}</div>
                <EmailAction />
            </div>
        </div>);
    }

    function CanonicalResidence() {
        return (<div className='quickfind-detail-section'>
            <div className='quickfind-detail-section-title'><p><strong>{clergySettings.canonicalResidenceLabel}</strong></p></div>
            <div className='quickfind-detail-section-inner'>{selectedClergy?.canonical_residence || <Wysiwyg content={clergySettings.canonicalResidenceEmptyMessage} />}</div>
        </div>);
    }

    function Position(position: PrincipalPosition) {
        if (!position) return null;

        return (<>
            <div key={position.position_title}>
                <div>{position.position_title}</div>
                <div>{`${position.start_date} - ${position.end_date}`}</div>
            </div>
        </>);
    }

    function InstitutionAction(institutionName: string, institutionId: string) {
        if (!institutionName) return null;

        if (institutionId) {
            return (<>
                <div> <button className='quickfind-detail-section-button-link' type='button' onClick={() => { onSelectParishById(institutionId); }} > {institutionName} </button></div>
            </>);
        }

        return (<>
            <div> {institutionName} </div>
        </>);
    }

    function Institution(institution: PrincipalPosition) {
        if (!institution?.employer_address) return null;

        const employerAddress = institution.employer_address;

        return (<div className='quickfind-detail-section-inner'>
            { InstitutionAction(institution.employer_name, institution.employer_id)}
            <div>{employerAddress.address1 || ''}</div>
            <div>{employerAddress.address2 || ''}</div>
            <div>{employerAddress.address3 || ''}</div>
            <div>{`${employerAddress.city ? `${employerAddress?.city},` : ''} ${employerAddress.state || ''} ${employerAddress.postal_code || ''}`}</div>
            <div>{institution?.employer_phone_number || ''}</div>
        </div>);
    }
    function PrincipalPositionsLabels() {
        return (
            <div className='quickfind-detail-section-columns'>
                <div className='quickfind-detail-section-title'><p><strong>{clergySettings.principalPositionsTitle}</strong></p></div>
                <div className='quickfind-detail-section-title'><p><strong>{clergySettings.parishInstitutionsTitle}</strong></p></div>
            </div>
        );

    }

    function PrincipalPositionsInfo() {
        if (selectedClergy?.principal_positions?.length > 0) {
            return (
                <div className='quickfind-detail-section-columns'>
                    {selectedClergy.principal_positions.map(position => <>{Position(position)}{Institution(position)}</>)}
                </div>
            );
        }
        return (
            <div className='quickfind-detail-section-columns'>
                <div><Wysiwyg content={clergySettings.principalPositionsEmptyMessage} /></div>
                <div><Wysiwyg content={clergySettings.parishInstitutionsEmptyMessage} /></div>
            </div>

        );
    }
    function RenderFieldInfo(title: string, content: string) {
        if (!content) return null;

        return (<>
            <div className='quickfind-detail-section-inner'>
                <div>{title}</div>
                <div>{content}</div>
            </div>
        </>);
    }

    function DiaconalInfo(diaconalData: DiaconalData) {
        if (!diaconalData) return null;

        return (<>
            { RenderFieldInfo(clergySettings.diaconalDateDetailLabel, diaconalData.deacon_date)}
            { RenderFieldInfo(clergySettings.bishopDetailLabel, diaconalData.bishop)}
            { RenderFieldInfo(clergySettings.dioceseDetailLabel, diaconalData.diocese)}
        </>);
    }

    function PriestingInfo(priestingData: PriestingData) {
        if (!priestingData) return null;

        return (<>
            { RenderFieldInfo(clergySettings.priestingDateDetailLabel, priestingData.priesting_date)}
            { RenderFieldInfo(clergySettings.bishopDetailLabel, priestingData.bishop)}
            { RenderFieldInfo(clergySettings.dioceseDetailLabel, priestingData.diocese)}
        </>);
    }

    function BishopConsecrationInfo(bishopConsecrationData: BishopConsecrationData) {
        if (!bishopConsecrationData) return null;

        return (<>
            { RenderFieldInfo(clergySettings.consecrationDateDetailLabel, bishopConsecrationData.consecration_date)}
            { RenderFieldInfo(clergySettings.consecratorsDetailLabel, bishopConsecrationData.consecrators?.join(', '))}
            { RenderFieldInfo(clergySettings.dioceseDetailLabel, bishopConsecrationData.diocese)}
        </>);
    }

    function OrdenationInformation() {
        if (selectedClergy?.ordination_information?.diaconal_data ||
            selectedClergy?.ordination_information?.priesting_data ||
            selectedClergy?.ordination_information?.bishop_consecration_data) {
            return (<>
                <div className='quickfind-detail-section-title'><p><strong>{clergySettings.ordinationInformationTitle}</strong></p></div>
                <div className="quickfind-detail-three-col-grid">
                    {DiaconalInfo(selectedClergy?.ordination_information?.diaconal_data)}
                    {PriestingInfo(selectedClergy?.ordination_information?.priesting_data)}
                    {BishopConsecrationInfo(selectedClergy?.ordination_information?.bishop_consecration_data)}
                </div>
            </>);
        }

        return (<>
            <div>{clergySettings.ordinationInformationTitle}</div>
            <div> <Wysiwyg content={clergySettings.ordinationInformationEmptyMessage} /></div>
        </>);
    }

    function TransferralInformation() {
        if (!selectedClergy?.transfer_data?.transfer_date &&
            !selectedClergy?.transfer_data?.transfer_bishop &&
            !selectedClergy?.transfer_data?.transferred_province) return null;

        const transferData = selectedClergy.transfer_data;

        return (<>
            <div>{clergySettings.transferalInformationTitle}</div>
            { RenderFieldInfo(clergySettings.transferalDateDetailLabel, transferData.transfer_date)}
            { RenderFieldInfo(clergySettings.transferalBishopDetailLabel, transferData.transfer_bishop)}
            { RenderFieldInfo(clergySettings.transferalProvidenceDetailLabel, transferData.transferred_province)}
        </>);
    }

    function ReceptionInformation() {
        if (!selectedClergy?.received_data?.received_date &&
            !selectedClergy?.received_data?.from &&
            !selectedClergy?.received_data?.receiving_bishop &&
            !selectedClergy?.received_data?.order) return null;

        const receivedData = selectedClergy.received_data;

        return (<>
            <div>{clergySettings.receptionInformationTitle}</div>
            { RenderFieldInfo(clergySettings.receptionDateDetailLabel, receivedData.received_date)}
            { RenderFieldInfo(clergySettings.receptionFromDetailLabel, receivedData.from)}
            { RenderFieldInfo(clergySettings.receptionOrdenDetailLabel, receivedData.order)}
            { RenderFieldInfo(clergySettings.receptionBishopDetailLabel, receivedData.receiving_bishop)}
        </>);
    }

    if (!selectedClergy) return null;

    return (
        <>
            <h3 className='ecd-page-content-title'>
                {clergySettings.pageTitle}
            </h3>
            <div className='ecd-page-content-section'>
                <h3>
                    {selectedClergy.full_name}
                </h3>
                <div className='quickfind-detail-section-columns'>
                    <PublishedAddress />
                    <CanonicalResidence />
                </div>
                <PrincipalPositionsLabels />
                <PrincipalPositionsInfo />
                <OrdenationInformation />
                <TransferralInformation />
                <ReceptionInformation />
                <NavigationButtons />
            </div>
        </>
    );
}

export default ClergyDetail;
